import type { PagedResultDto } from "@services/dto/pagedResultDto"
import saleEventService from "@services/saleEvent/saleEventService"
import { action, makeAutoObservable, observable } from "mobx"

class SaleEventStore {
  @observable isLoading!: boolean
  @observable tableData!: PagedResultDto<any>
  @observable detailData!: any
  @observable auditLogData!: any
  @observable unitsData!: PagedResultDto<any>
  @observable bookingsData!: PagedResultDto<any>
  @observable historyData!: PagedResultDto<any>
  @observable bookingCustomerData!: any
  @observable previewData!: any
  constructor() {
    makeAutoObservable(this)

    this.tableData = { items: [], totalCount: 0 }
    this.detailData = {}
    this.auditLogData = []
    this.unitsData = { items: [], totalCount: 0 }
    this.bookingsData = { items: [], totalCount: 0 }
    this.historyData = { items: [], totalCount: 0 }
    this.bookingCustomerData = {}
    this.previewData = {}
  }

  @action
  async createOrUpdate(body: any) {
    if (!body.id) {
      const result = await saleEventService.create(body)
      this.detailData = result
      this.tableData.items.push(result)
      return result
    } else {
      const result = await saleEventService.update(body)
      this.tableData.items = this.tableData.items.map((x) => {
        if (x.id === body.id) x = result
        return x
      })
      return result
    }
  }

  @action
  async saveCheckinSetting(body: {
    salesEventId: number
    requireCheckin: boolean
    requireBallotting: boolean
    requireCheckinSTT: boolean
    requireCheckinRoom: boolean
    jsonSetting: null | string
  }) {
    return await saleEventService.saveCheckinSetting(body)
  }

  @action
  async getCheckinSetting(params: { salesEventId: number }) {
    return await saleEventService.getCheckinSetting(params)
  }

  @action
  async newDetail() {
    this.detailData = {
      id: 0,
    }
  }

  @action
  async active(id: number, isActive) {
    await saleEventService.active(id, isActive)
  }
  @action
  async bulkActive(id: number[]) {
    await saleEventService.bulkActive(id)
  }
  @action
  async get(id: number) {
    const result = await saleEventService.get(id)
    this.detailData = result
  }

  @action
  async getAll(params: any) {
    this.isLoading = true
    const result = await saleEventService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.tableData = result
  }
  @action
  async getAuditLog(id: any) {
    const result = await saleEventService.getAuditLog(id)

    this.auditLogData = result
  }

  @action
  clearUnitsData() {
    this.unitsData = { items: [], totalCount: 0 }
    this.isLoading = false
  }

  clearBookingsData() {
    this.bookingsData = { items: [], totalCount: 0 }
    this.isLoading = false
  }

  @action
  async getUnits(params: any) {
    this.isLoading = true
    this.unitsData = { items: [], totalCount: 0 }
    
    try {
      const result = await saleEventService.getUnits(params)
      this.unitsData = result
    } catch (error) {
      this.unitsData = { items: [], totalCount: 0 }
      console.error('Error fetching units:', error)
    } finally {
      this.isLoading = false
    }
  }

  @action
  async getBookingInSaleEvent(body: any) {
    this.isLoading = true
    this.bookingsData = { items: [], totalCount: 0 }
    try {
      const result = await saleEventService.getBookingInSaleEvent(body)
      this.bookingsData = result
    } catch (error) {
      this.bookingsData = { items: [], totalCount: 0 }
      console.error('Error fetching bookings:', error)
    } finally {
      this.isLoading = false
    }
  }

  @action
  async getSaleEventHistory(params: any) {
    this.isLoading = true
    this.historyData = { items: [], totalCount: 0 }
    try {
      const result = await saleEventService.getSaleEventHistory(params)
      this.historyData = result
    } catch (error) {
      this.historyData = { items: [], totalCount: 0 }
      console.error('Error fetching history:', error)
    } finally {
      this.isLoading = false
    }
  }

  @action
  async resendNotification(params: any) {
    this.isLoading = true
    await saleEventService.resendNotification(params)
      .finally(() => (this.isLoading = false))
  }

  @action
  async exportSaleEventBookingExportTicket(params: any, body: any) {
    this.isLoading = true
    const res = await saleEventService.exportSaleEventBookingExportTicket(params, body)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async getListBookingTicket(params: any) {
    this.isLoading = true
    const res = await saleEventService.getListBookingTicket(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action 
  async BookingInvitationPickProduct(params: any) {
    this.isLoading = true
    const res = await saleEventService.BookingInvitationPickProduct(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async changeSaleEventBookingStatus(body: any) {
    this.isLoading = true
    const res = await saleEventService.changeSaleEventBookingStatus(body)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async checkInBookingInvitation(params: any) {
    this.isLoading = true
    const res = await saleEventService.checkInBookingInvitation(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async getCurrentBookingProcess(params: any) {
    this.isLoading = true
    const res = await saleEventService.getCurrentBookingProcess(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async getBookingCustomerJoin(params: any) {
    this.isLoading = true
    try {
      const res = await saleEventService.getBookingCustomerJoin(params)
      this.bookingCustomerData = res || {}
      return res
    } catch (error) {
      this.bookingCustomerData = {}
      throw error
    } finally {
      this.isLoading = false
    }
  }

  @action
  async updateRepresentationCustomer(body: any) {
    this.isLoading = true
    const res = await saleEventService.updateRepresentationCustomer(body)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async updateStatusEvent(body: any) {
    this.isLoading = true
    const res = await saleEventService.updateStatusEvent(body)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async viewContentDetailNotification(params: any) {
    this.isLoading = true
    const res = await saleEventService.viewContentDetailNotification(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  setPreviewData(data: any) {
    this.previewData = data
  }

  @action
  async getSalesEventConfiguration(params: any) {
    this.isLoading = true
    const res = await saleEventService.getSalesEventConfiguration(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async createOrUpdateSalesEventConfiguration(body: any) {
    this.isLoading = true
    const res = await saleEventService.createOrUpdateSalesEventConfiguration(body)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async updatePickProductStatus(body: any) {
    this.isLoading = true
    const res = await saleEventService.updatePickProductStatus(body)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async verifySalesEventCheckList(params: any) {
    this.isLoading = true
    const res = await saleEventService.verifySalesEventCheckList(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async clearBookingUnit(params: any) {
    this.isLoading = true
    const res = await saleEventService.clearBookingUnit(params)
      .finally(() => (this.isLoading = false))
    return res
  }

  @action
  async getListStackingPlan(params: any) {
    this.isLoading = true
    try {
      return await saleEventService.getListStackingPlan(params)
    } finally {
      this.isLoading = false
    }
  }
}

export default SaleEventStore
